import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Mail, RefreshCw, Download, AlertTriangle, ShieldCheck, Loader2 } from "lucide-react";
import {
  adminListLlbEmailLogs,
  adminBulkRetryFailedTailoredNotes,
  adminListLlbRetryAudit,
} from "@/lib/api/llb-tutoring.functions";

export const Route = createFileRoute("/_authenticated/portal/admin/llb-email-logs")({
  component: AdminLlbEmailLogs,
});

function ts(s?: string | null) {
  return s ? new Date(s).toLocaleString("en-ZA") : "—";
}

function badge(s?: string | null) {
  const map: Record<string, string> = {
    sent: "bg-emerald-100 text-emerald-700",
    delivered: "bg-emerald-100 text-emerald-700",
    pending: "bg-sky-100 text-sky-700",
    queued: "bg-sky-100 text-sky-700",
    failed: "bg-rose-100 text-rose-700",
    dlq: "bg-rose-100 text-rose-700",
    suppressed: "bg-amber-100 text-amber-700",
    bounced: "bg-rose-100 text-rose-700",
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${map[s ?? ""] ?? "bg-muted text-muted-foreground"}`}>
      {s ?? "—"}
    </span>
  );
}

function csvEscape(v: any) {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename: string, headers: string[], rows: (string | number | null)[][]) {
  const lines = [headers.map(csvEscape).join(",")]
    .concat(rows.map((r) => r.map(csvEscape).join(",")))
    .join("\n");
  const blob = new Blob([lines], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function AdminLlbEmailLogs() {
  const qc = useQueryClient();
  const listFn = useServerFn(adminListLlbEmailLogs);
  const bulkRetryFn = useServerFn(adminBulkRetryFailedTailoredNotes);
  const auditFn = useServerFn(adminListLlbRetryAudit);

  const [search, setSearch] = useState("");
  const [templateName, setTemplateName] = useState<"" | "llb-tailored-notes" | "llb-daily-tip">("");
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const q = useQuery({
    queryKey: ["admin-llb-email-logs", { search, templateName, status, from, to }],
    queryFn: () =>
      listFn({
        data: {
          search: search || undefined,
          templateName: templateName || undefined,
          status: status || undefined,
          from: from || undefined,
          to: to || undefined,
        },
      }),
  });

  const auditQ = useQuery({
    queryKey: ["admin-llb-retry-audit", { search, from, to }],
    queryFn: () =>
      auditFn({
        data: {
          search: search || undefined,
          from: from || undefined,
          to: to || undefined,
        },
      }),
  });

  const rows = (q.data?.rows ?? []) as any[];
  const auditRows = (auditQ.data?.rows ?? []) as any[];

  const stats = {
    total: rows.length,
    sent: rows.filter((r) => r.status === "sent" || r.status === "delivered").length,
    failed: rows.filter((r) => r.status === "failed" || r.status === "dlq" || r.status === "bounced").length,
    pending: rows.filter((r) => r.status === "pending" || r.status === "queued").length,
  };

  const failedRows = rows.filter(
    (r) =>
      (r.template_name === "llb-tailored-notes") &&
      (r.status === "failed" || r.status === "dlq" || r.status === "bounced"),
  );

  async function onBulkRetry() {
    if (!confirm(`Retry ${failedRows.length} failed tailored-notes email(s) matching current filters?`)) return;
    setBulkBusy(true);
    try {
      const res: any = await bulkRetryFn({
        data: {
          moduleCode: undefined,
          from: from || undefined,
          to: to || undefined,
          limit: 100,
        },
      });
      toast.success("Bulk retry complete", {
        description: `${res.succeeded}/${res.attempted} queued, ${res.failed} failed`,
      });
      qc.invalidateQueries({ queryKey: ["admin-llb-email-logs"] });
      qc.invalidateQueries({ queryKey: ["admin-llb-retry-audit"] });
    } catch (e: any) {
      toast.error("Bulk retry failed", { description: e?.message ?? String(e) });
    } finally {
      setBulkBusy(false);
    }
  }

  function onExportCsv() {
    const headers = [
      "created_at",
      "template",
      "recipient",
      "module_code",
      "status",
      "message_id",
      "error",
      "metadata",
    ];
    const csvRows = rows.map((r) => {
      const meta = r.metadata ?? {};
      const moduleCode = meta.moduleCode || meta.templateData?.moduleCode || "";
      return [
        r.created_at ?? "",
        r.template_name ?? "",
        r.recipient_email ?? "",
        moduleCode,
        r.status ?? "",
        r.message_id ?? "",
        r.error_message ?? "",
        JSON.stringify(meta),
      ];
    });
    downloadCsv(`llb-email-logs-${new Date().toISOString().slice(0, 10)}.csv`, headers, csvRows);
  }

  function onExportAuditCsv() {
    const headers = [
      "created_at",
      "admin_email",
      "admin_name",
      "student_email",
      "student_name",
      "module_code",
      "previous_status",
      "new_status",
      "recipient_email",
      "message_id",
      "error_message",
      "note_id",
      "source",
    ];
    const csvRows = auditRows.map((r: any) => [
      r.created_at ?? "",
      r.admin?.email ?? "",
      r.admin?.full_name ?? "",
      r.student?.email ?? "",
      r.student?.full_name ?? "",
      r.module_code ?? "",
      r.previous_status ?? "",
      r.new_status ?? "",
      r.recipient_email ?? "",
      r.message_id ?? "",
      r.error_message ?? "",
      r.note_id ?? "",
      r.metadata?.source ?? "",
    ]);
    downloadCsv(`llb-retry-audit-${new Date().toISOString().slice(0, 10)}.csv`, headers, csvRows);
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl text-navy flex items-center gap-2">
            <Mail className="h-5 w-5" /> LLB email queue logs
          </h1>
          <p className="text-sm text-muted-foreground">
            Per-student per-module email send log for tailored notes and daily tips — with delivery timestamps.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={onExportCsv}>
            <Download className="h-4 w-4 mr-1" /> Export CSV
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={bulkBusy || failedRows.length === 0}
            onClick={onBulkRetry}
            title={failedRows.length === 0 ? "No failed tailored-notes in current view" : ""}
          >
            {bulkBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-1" />}
            Retry failed ({failedRows.length})
          </Button>
          <Button size="sm" variant="outline" onClick={() => q.refetch()}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </header>

      <section className="grid gap-3 sm:grid-cols-4">
        <Stat label="Unique emails" value={stats.total} />
        <Stat label="Delivered" value={stats.sent} />
        <Stat label="Failed" value={stats.failed} />
        <Stat label="Pending" value={stats.pending} />
      </section>

      <div className="rounded-xl border border-border bg-card p-3 grid gap-2 md:grid-cols-6">
        <Input className="md:col-span-2" placeholder="Search recipient / message id / metadata…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value as any)}
        >
          <option value="">All templates</option>
          <option value="llb-tailored-notes">llb-tailored-notes</option>
          <option value="llb-daily-tip">llb-daily-tip</option>
        </select>
        <select
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="sent">sent</option>
          <option value="delivered">delivered</option>
          <option value="pending">pending</option>
          <option value="failed">failed</option>
          <option value="dlq">dlq</option>
          <option value="suppressed">suppressed</option>
          <option value="bounced">bounced</option>
        </select>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No log entries match.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-left">Template</th>
                <th className="px-3 py-2 text-left">Recipient</th>
                <th className="px-3 py-2 text-left">Module</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Error / message id</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const meta = r.metadata ?? {};
                const moduleCode = meta.moduleCode || meta.templateData?.moduleCode || "—";
                return (
                  <tr key={r.id} className="border-t border-border align-top">
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{ts(r.created_at)}</td>
                    <td className="px-3 py-2 text-xs font-mono">{r.template_name}</td>
                    <td className="px-3 py-2 text-xs">{r.recipient_email}</td>
                    <td className="px-3 py-2 text-xs font-mono">{moduleCode}</td>
                    <td className="px-3 py-2">{badge(r.status)}</td>
                    <td className="px-3 py-2 text-[11px] text-muted-foreground">
                      {r.error_message ? <span className="text-rose-700">{r.error_message}</span> : <span className="font-mono">{r.message_id}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <section className="space-y-3 pt-6 border-t border-border">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-xl text-navy flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" /> Admin retry audit log
            </h2>
            <p className="text-sm text-muted-foreground">
              Every admin "retry email" action — who, when, module, student, old → new status, message id.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={onExportAuditCsv} disabled={auditRows.length === 0}>
            <Download className="h-4 w-4 mr-1" /> Export audit CSV
          </Button>
        </header>

        {auditQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : auditRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No retry actions yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">Admin</th>
                  <th className="px-3 py-2 text-left">Student</th>
                  <th className="px-3 py-2 text-left">Module</th>
                  <th className="px-3 py-2 text-left">Old → New</th>
                  <th className="px-3 py-2 text-left">Message / error</th>
                </tr>
              </thead>
              <tbody>
                {auditRows.map((r) => (
                  <tr key={r.id} className="border-t border-border align-top">
                    <td className="px-3 py-2 text-xs whitespace-nowrap text-muted-foreground">{ts(r.created_at)}</td>
                    <td className="px-3 py-2 text-xs">
                      <div>{r.admin?.full_name ?? "—"}</div>
                      <div className="text-[11px] text-muted-foreground">{r.admin?.email ?? r.admin_id}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div>{r.student?.full_name ?? "—"}</div>
                      <div className="text-[11px] text-muted-foreground">{r.student?.email ?? r.recipient_email ?? r.student_user_id}</div>
                    </td>
                    <td className="px-3 py-2 text-xs font-mono">{r.module_code ?? "—"}</td>
                    <td className="px-3 py-2 text-xs">
                      <div className="flex items-center gap-1">
                        {badge(r.previous_status)} <span className="text-muted-foreground">→</span> {badge(r.new_status)}
                      </div>
                      {r.metadata?.source && (
                        <div className="text-[10px] mt-1 text-muted-foreground">via {r.metadata.source}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[11px]">
                      {r.error_message ? (
                        <span className="text-rose-700">{r.error_message}</span>
                      ) : (
                        <span className="font-mono text-muted-foreground">{r.message_id ?? "—"}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: any }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-navy">{value}</p>
    </div>
  );
}
